/**
 * T-049: idempotency monitor (read + bounded force-reap).
 *
 * Tiles: TTL, in-flight pending count, last-24h conflicts.
 * Disclosure: 20 most-recent conflicts (key prefix only).
 * Action: Force-reap (superadmin, confirmation dialog before invoking).
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import {
  getIdempotencyStatus,
  forceReapIdempotency,
} from '@/lib/pocketSettingsApi';
import type { PocketIdempotencyStatus } from '@/types/pocketSettings';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function IdempotencyMonitorCard() {
  const { isSuperadmin } = useRBAC();
  const [status, setStatus] = useState<PocketIdempotencyStatus | null>(null);
  const [reaping, setReaping] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    getIdempotencyStatus()
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        // Non-fatal; tile values stay at "—".
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function handleReap() {
    setReaping(true);
    try {
      const result = await forceReapIdempotency();
      toast.success(
        `Reaped ${result.reaped} idempotency ${result.reaped === 1 ? 'entry' : 'entries'}.`,
      );
      setConfirmOpen(false);
      refresh();
    } catch {
      toast.error('Reap failed.');
    } finally {
      setReaping(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <CardTitle className="text-base font-semibold">Idempotency monitor</CardTitle>
        {isSuperadmin ? (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={reaping}
          >
            Force reap
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Replay-safe retry keys. <span className="text-foreground font-medium">In-flight</span> = currently processing.
          <span className="text-foreground font-medium"> Conflicts</span> = retries arrived with a different body
          (client bug suspect). Small numbers = healthy.
        </p>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Tile label="TTL" value={status ? `${status.ttl_hours}h` : '—'} />
          <Tile
            label="In-flight"
            value={status ? String(status.in_flight) : '—'}
          />
          <Tile
            label="Conflicts (24h)"
            value={status ? String(status.conflicts_24h) : '—'}
          />
        </div>

        <details>
          <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground">
            Recent conflicts (last 24 hours)
          </summary>
          <div className="mt-3">
            {!status || status.recent_conflicts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No replay-mismatches in the last 24 hours.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key prefix</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {status.recent_conflicts.map((row, idx) => (
                    <TableRow key={`${row.key_prefix}-${idx}`}>
                      <TableCell className="font-mono text-xs">
                        {row.key_prefix}…
                      </TableCell>
                      <TableCell>{row.source_table || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatDate(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </details>

        <p className="mt-4 text-xs text-muted-foreground">
          <strong>What "Force reap" does:</strong> deletes expired keys (older
          than the TTL) plus any "in-flight" keys stuck for more than 10
          minutes. Same exact rule as the cron job — clicking it just runs
          that rule on demand. Useful if the cron is behind. Every reap is
          audited.
        </p>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Force-reap idempotency keys?</DialogTitle>
            <DialogDescription>
              Runs the same bounded predicate as the cron reaper (expired rows + stuck-
              pending older than 10 minutes). No new destructive primitive — just an
              on-demand invocation. Audited.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={reaping}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReap} disabled={reaping}>
              {reaping ? 'Reaping…' : 'Reap'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/50 p-4 bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}
